// ── 연차 파생 계산 훅 ────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { matchesLeaveFilter } from './utils';

/**
 * 연차 목록으로부터 파생 계산값 (누적 잔여, 필터 정의, 통계, 필터링된 목록) 제공
 * @param {{ leaveList: Array, activeLeaveFilters: string[] }} params
 */
export function useLeaveCalcs({ leaveList, activeLeaveFilters }) {
  // ── 연차 내역: 행별 누적 잔여일수 계산 ──────────────────────────────────
  const leaveRowsWithRemaining = useMemo(() => {
    const rows          = Array.isArray(leaveList) ? leaveList : [];
    const generatedTypes   = new Set(['생성', '자동생성', '포상', 'Bonus']);
    const settleTypes      = new Set(['정산', 'Settle']);
    const paidCategories   = new Set(['유급', 'Paid']);
    const settleCategories = new Set(['정산', 'Settle']);

    // 오름차순 정렬 (날짜 → id 순)
    // 문자열 비교: YYYY-MM-DD 형식은 사전순으로 날짜순과 동일 (new Date 직접 사용 금지)
    const ascRows = [...rows].sort((a, b) => {
      const da = String(a.date || '');
      const db = String(b.date || '');
      if (da !== db) return da < db ? -1 : 1;
      return Number(a.id || 0) - Number(b.id || 0);
    });

    // 행별로 누적 잔여일수 계산
    let running = 0;
    const remainById = new Map();
    ascRows.forEach((row) => {
      if (row.is_correction) { remainById.set(row.id, null); return; }
      const count    = Number(row.count || 0);
      const type     = String(row.type || '').trim();
      const category = String(row.category || '').trim();
      const balanceEffect = String(row.leave_balance_effect || 'legacy');
      if (balanceEffect === 'grant') running += count;
      else if (balanceEffect === 'deduct' || balanceEffect === 'settlement') running -= count;
      else if (balanceEffect === 'legacy' && generatedTypes.has(type)) running += count;
      else if (balanceEffect === 'legacy' && (settleTypes.has(type) || settleCategories.has(category))) running -= count;
      else if (balanceEffect === 'legacy' && paidCategories.has(category)) running -= count;
      remainById.set(row.id, Number(running.toFixed(1)));
    });

    return rows.map((row) => ({
      ...row,
      remaining_after: remainById.has(row.id) ? remainById.get(row.id) : null,
    }));
  }, [leaveList]);

  // ── 연차 필터 정의 (유형별 색상 + 레이블) ──────────────────────────────
  const leaveFilterDefs = useMemo(() => [
    { key: 'generated', label: '생성',   color: '#1d4ed8' },
    { key: 'used',      label: '사용',   color: '#0f766e' },
    { key: 'paid_recognized', label: '유급인정', color: '#2563eb' },
    { key: 'unpaid',    label: '무급',   color: '#9a3412' },
    { key: 'settled',   label: '정산',   color: '#7c3aed' },
    { key: 'family',    label: '경조사', color: '#be185d' },
    { key: 'bonus',     label: '포상',   color: '#065f46' },
    { key: 'corrected', label: '정정',   color: 'var(--text-muted)' },
  ], []);

  // ── 연차 유형별 합산 통계 ─────────────────────────────────────────────
  const leaveSummaryStats = useMemo(() => {
    const stats = { generated: 0, used: 0, paid_recognized: 0, unpaid: 0, settled: 0, family: 0, bonus: 0, corrected: 0 };
    leaveRowsWithRemaining.forEach((row) => {
      const n = Number(row.count || 0);
      if (matchesLeaveFilter(row, 'generated')) stats.generated += n;
      if (matchesLeaveFilter(row, 'used'))      stats.used      += n;
      if (matchesLeaveFilter(row, 'paid_recognized')) stats.paid_recognized += n;
      if (matchesLeaveFilter(row, 'unpaid'))    stats.unpaid    += n;
      if (matchesLeaveFilter(row, 'settled'))   stats.settled   += n;
      if (matchesLeaveFilter(row, 'family'))    stats.family    += n;
      if (matchesLeaveFilter(row, 'bonus'))     stats.bonus     += n;
      if (matchesLeaveFilter(row, 'corrected')) stats.corrected += n;
    });
    return stats;
  }, [leaveRowsWithRemaining]);

  // ── 합계가 0이어도 해당 유형 내역이 있으면 필터 버튼을 노출하기 위한 존재 여부 ──
  const leaveSummaryPresence = useMemo(() => {
    const presence = { generated: false, used: false, paid_recognized: false, unpaid: false, settled: false, family: false, bonus: false, corrected: false };
    leaveRowsWithRemaining.forEach((row) => {
      Object.keys(presence).forEach((key) => {
        if (!presence[key] && matchesLeaveFilter(row, key)) presence[key] = true;
      });
    });
    return presence;
  }, [leaveRowsWithRemaining]);

  // ── 활성 필터에 따른 연차 내역 필터링 ────────────────────────────────
  const filteredLeaveRows = useMemo(() => {
    if (activeLeaveFilters.length === 0) return leaveRowsWithRemaining;
    return leaveRowsWithRemaining.filter((row) =>
      activeLeaveFilters.some((key) => matchesLeaveFilter(row, key))
    );
  }, [leaveRowsWithRemaining, activeLeaveFilters]);

  return { leaveRowsWithRemaining, leaveFilterDefs, leaveSummaryStats, leaveSummaryPresence, filteredLeaveRows };
}
